import { useEffect, useRef } from 'react';
import { type SubscriptionType } from '@doxyme/extensions-graphql-types';
import {
  useRegistryForPatientSingleton,
  useRegistrySingleton,
} from '@doxyme/extensions-bridge-react';
import { useLocale } from '@doxyme/locale';
import { type UserSubscriptionType } from '../../../services';
import {
  bridge,
  registry,
  registryWithInstall,
} from '../../../features/extensions/instances';
import { getWaitingRoom } from '../../../features/waitingRoom/waitingRoom.selectors';
import { RealtimeMessageType } from '../../../features/realtime/realtime.types';
import { subscribeToMessage } from '../../../features/realtime/realtime.subscribe';
import { getMemberIds } from '../../../features/realtime/realtime.selectors';
import * as realtime from '../../../features/realtime/realtime.slice';
import { useAppSelector, useAppDispatch } from '../../useReduxToolkit';
import { getRegistryCompatibleISO } from './getRegistryCompatibleISO';
import { useGetAccessToken } from '../../useCredentials';
import { usePatientPaidStatus } from '../../../hooks/usePatientPaidStatus';

export const getRegistrySubscriptionType = (
  subscriptionType?: UserSubscriptionType,
): SubscriptionType | undefined => {
  return subscriptionType
    ? ((subscriptionType.charAt(0).toUpperCase() +
        subscriptionType.slice(1)) as SubscriptionType)
    : undefined;
};

export function useExtensionsSetupRegistry(): void {
  const registrySingleton = useRegistrySingleton();
  const { locale } = useLocale();
  const token = useGetAccessToken();

  const dispatch = useAppDispatch();
  const memberIds = useAppSelector(getMemberIds);

  const sendUpdateRef = useRef<() => Promise<void> | undefined>(
    () => undefined,
  );
  sendUpdateRef.current = async (): Promise<void> => {
    await dispatch(
      realtime.sendMessageMany({
        ids: memberIds,
        message: {
          messageType: RealtimeMessageType.ExtensionRegistryUpdate,
        },
      }),
    );
  };

  useEffect(() => {
    if (registrySingleton.isInitialized) {
      registry.initApiClient(`Bearer ${token}`);

      registrySingleton.setDependencies({
        bridge,
        registry,
        onRegistryUpdate: async (): Promise<void> => sendUpdateRef.current?.(),
        locale: getRegistryCompatibleISO(locale),
      });

      void registrySingleton.refresh();
    }
  }, [registrySingleton.isInitialized, locale]);

  useEffect(() => {
    if (!registrySingleton.isInitialized) {
      return;
    }

    registryWithInstall.setInstallApp(registrySingleton.activate);

    return () => {
      registryWithInstall.clearInstallApp();
    };
  }, [registrySingleton.isInitialized, registrySingleton.activate]);
}

export function useExtensionsSetupRegistryForPatient(): void {
  const registrySingleton = useRegistryForPatientSingleton();
  const waitingRoom = useAppSelector(getWaitingRoom);
  const { roomAdminSubscriptionType } = usePatientPaidStatus(waitingRoom);

  useEffect(() => {
    if (registrySingleton.isInitialized) {
      registrySingleton.setDependencies(
        bridge,
        registry,
        waitingRoom.roomAdmin.id.toString(),
        getRegistrySubscriptionType(roomAdminSubscriptionType),
      );

      void registrySingleton.refresh();
    }
  }, [registrySingleton.isInitialized]);

  useEffect(() => {
    const unsubscribe = subscribeToMessage({
      messageType: RealtimeMessageType.ExtensionRegistryUpdate,
      callback: () => {
        void registrySingleton.refresh();
      },
    });

    return unsubscribe;
  }, [registrySingleton.refresh]);
}
